import {useEffect} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {InsideModalProvider} from 'contexts/ModalContext'

/**
 * Visual shell for a route-modal opened over /map.
 *
 * Renders a fullscreen scrim, a content panel, and a close button. The
 * caller's children are wrapped in InsideModalProvider so any nested
 * Navbar / Footer instances self-hide — that's how we re-use the
 * existing detail-page components (SpacePage, JobPage, ClassPage,
 * BusinessProfile) without forking them into modal-mode variants.
 *
 * Closing strategy: pop the modal route off history with navigate(-1)
 * so the URL flips back to whatever was beneath. This relies on the
 * caller having opened the modal via a Link with
 * `state.backgroundLocation = currentLocation`, which is what the map
 * cards do. If the user lands here via a fresh tab (no history to pop)
 * we fall back to navigating to /map so they're not stranded.
 */
const ModalShell = ({children}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const close = () => {
    if (location.state?.backgroundLocation) {
      navigate(-1)
    } else {
      navigate('/map', {replace: true})
    }
  }

  // Body scroll lock while open — without this the underlying map
  // keeps scrolling under the modal on touch / mousewheel.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  // Esc closes the modal. Keyup listener (not keydown) so we don't
  // race with input fields swallowing the same event.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keyup', onKey)
    return () => window.removeEventListener('keyup', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-center">
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className="absolute inset-0 bg-black/45"
      />
      {/* Sheet */}
      <div className="relative z-10 w-full max-w-[1200px] mx-auto my-0 sm:my-6 bg-white sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-end px-3 py-2 border-b border-gray-100 bg-white sticky top-0 z-10">
          <button
            type="button"
            onClick={close}
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-sharpenn-charcoal"
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <InsideModalProvider>{children}</InsideModalProvider>
        </div>
      </div>
    </div>
  )
}

export default ModalShell
